/**
 * Workshop Form Component
 * Form for creating and editing workshops
 */

import { useState } from "react";
import { Modal } from "./Modal";
import { Button } from "@components/common";
import type {
  Workshop,
  WorkshopStatus,
  WorkshopDifficulty,
} from "@/types/models";
import type { WorkshopCreateRequest, WorkshopUpdateRequest } from "@/types/api";

const STATUS_OPTIONS: { value: WorkshopStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

const DIFFICULTY_OPTIONS: { value: WorkshopDifficulty; label: string }[] = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

interface FormState {
  title: string;
  description: string;
  status: WorkshopStatus;
  start_date: string;
  end_date: string;
  // Phase 2 fields for task_040 support
  estimated_minutes: string;
  difficulty_level: WorkshopDifficulty | "";
  prerequisites: string;
  learning_objectives: string;
}

const DEFAULT_FORM_STATE: FormState = {
  title: "",
  description: "",
  status: "draft",
  start_date: "",
  end_date: "",
  estimated_minutes: "",
  difficulty_level: "",
  prerequisites: "",
  learning_objectives: "",
};

function getInitialState(
  mode: "create" | "edit",
  workshop?: Workshop | null,
): FormState {
  if (mode === "edit" && workshop) {
    return {
      title: workshop.title,
      description: workshop.description || "",
      status: workshop.status,
      start_date: workshop.start_date ? workshop.start_date.split("T")[0] : "",
      end_date: workshop.end_date ? workshop.end_date.split("T")[0] : "",
      estimated_minutes: workshop.estimated_minutes?.toString() || "",
      difficulty_level: workshop.difficulty_level || "",
      prerequisites: workshop.prerequisites || "",
      learning_objectives: workshop.learning_objectives?.join("\n") || "",
    };
  }
  return { ...DEFAULT_FORM_STATE };
}

interface WorkshopFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: WorkshopCreateRequest | WorkshopUpdateRequest,
  ) => Promise<void>;
  isLoading: boolean;
  workshop?: Workshop | null;
  mode: "create" | "edit";
}

export function WorkshopForm({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  workshop,
  mode,
}: WorkshopFormProps) {
  // Form state initialized from props - parent should use key prop to reset
  const [form, setForm] = useState<FormState>(() =>
    getInitialState(mode, workshop),
  );

  // Helper to update individual form fields
  const updateField = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Parse learning objectives from text (one per line or comma-separated)
    const objectives = form.learning_objectives
      .split(/[\n,]/)
      .map((s) => s.replace(/^[-*•]\s*/, "").trim())
      .filter(Boolean);

    const data: WorkshopCreateRequest = {
      title: form.title,
      description: form.description || undefined,
      status: form.status,
      start_date: form.start_date || undefined,
      end_date: form.end_date || undefined,
      // Phase 2 fields
      estimated_minutes: form.estimated_minutes
        ? parseInt(form.estimated_minutes, 10)
        : undefined,
      difficulty_level: form.difficulty_level || undefined,
      prerequisites: form.prerequisites || undefined,
      learning_objectives: objectives.length > 0 ? objectives : undefined,
    };

    await onSubmit(data);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "create" ? "Create Workshop" : "Edit Workshop"}
      description={
        mode === "create"
          ? "Add a new workshop to the platform"
          : "Update workshop details"
      }
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>
            {mode === "create" ? "Create Workshop" : "Save Changes"}
          </Button>
        </div>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4 max-h-[60vh] overflow-y-auto pr-2"
      >
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Title *
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., Introduction to Agent Development"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="A brief description of this workshop..."
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <select
            value={form.status}
            onChange={(e) =>
              updateField("status", e.target.value as WorkshopStatus)
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Draft workshops are not visible to participants.
          </p>
        </div>

        {/* Dates Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => updateField("start_date", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={form.end_date}
              onChange={(e) => updateField("end_date", e.target.value)}
              min={form.start_date || undefined}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Duration and Difficulty Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Estimated Duration (minutes)
            </label>
            <input
              type="number"
              value={form.estimated_minutes}
              onChange={(e) => updateField("estimated_minutes", e.target.value)}
              min={0}
              placeholder="e.g., 120"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Difficulty Level
            </label>
            <select
              value={form.difficulty_level}
              onChange={(e) =>
                updateField(
                  "difficulty_level",
                  e.target.value as WorkshopDifficulty | "",
                )
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Select difficulty...</option>
              {DIFFICULTY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Prerequisites */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Prerequisites
          </label>
          <textarea
            value={form.prerequisites}
            onChange={(e) => updateField("prerequisites", e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Required knowledge or skills before starting this workshop..."
          />
        </div>

        {/* Learning Objectives */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Learning Objectives
          </label>
          <textarea
            value={form.learning_objectives}
            onChange={(e) => updateField("learning_objectives", e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Enter one objective per line, e.g.:&#10;- Understand basic agent concepts&#10;- Build your first AI agent&#10;- Configure agent tools"
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter one objective per line. Supports bullet points (-, *, •) or
            plain text.
          </p>
        </div>
      </form>
    </Modal>
  );
}
